import { useEffect, useState } from "react";
import ResearchObjectives from "src/components/@ai-liberman/qualitative-agent/research-objectives";
import ParticipantDistribution from "src/components/@ai-liberman/qualitative-agent/participant-distribution";
import BannerSlide from "src/components/@ai-liberman/qualitative-agent/banner-slide";
import libermanLogo from "src/assets/ai-liberman/ai-lieberman-logo.png"
import ThemeFrequencies from "src/components/@ai-liberman/qualitative-agent/theme-frequencies";
import EmotionalImpact from "src/components/@ai-liberman/qualitative-agent/emotional-Impact";
import AIAdoptation from "src/components/@ai-liberman/qualitative-agent/ai-adoptation";
import { aiJson } from "src/components/@ai-liberman/helper/mock";
import IdentifiedRisk from "src/components/@ai-liberman/qualitative-agent/identified-risk";
import FutureExpectation from "src/components/@ai-liberman/qualitative-agent/future-expectation";
import FutureImpact from "src/components/@ai-liberman/qualitative-agent/future-impact";
import Recommendations from "src/components/@ai-liberman/qualitative-agent/recommndations";
import Healthcare from "src/components/@ai-liberman/qualitative-agent/healthcare";
import RiskMisinterpretation from "src/components/@ai-liberman/qualitative-agent/risk-misinterpretation";
import { getPPTStatusV2, getPPTStatusV3 } from "../../ai-agent/action";
import { useAppDispatch } from "src/hooks/redux";
import { useLocation, useSearchParams } from "react-router-dom";
import DotLoader from "src/components/reusable/dot-loader";
import ThankYouSlide from "src/components/@ai-liberman/qualitative-agent/thankyou-slide";

// 1️⃣ Define your slides array (outside the component)
interface PptResponseType {
  research_objectives: any[];
  methodology_details:any[];
  participant_profiles:any[];
  participant_distribution:any[];
  ai_adoption_levels:any[];
  current_ai_usage_patterns:any[];
  theme_significance:any[];
  cognitive_and_emotional_effects:any[];
  identified_risks:any[];
  future_capability_scores:any[];
  future_impact_matrix:any[];
  risks_misinterpretations_guardrails:any[];
  recommendations_set:any[];
  future_expectations:any[];
}

const PercentageLoader = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) return 0;
        return prev + Math.random() * 15;
      });
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
      <div className="w-full max-w-md text-center">
        <div className="text-4xl font-bold text-green-800 mb-4">
          {Math.min(Math.floor(progress), 100)}%
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2 mb-4 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-lime-500 to-lime-800 h-2 rounded-full transition-all duration-500"
            style={{width: `${Math.min(progress, 100)}%`}}
          ></div>
        </div>
        <p className="text-slate-600">Generating your deck...</p>
        <p className="text-sm text-slate-400 mt-2">This usually takes 10-15 seconds</p>
      </div>
  );
};

export default function QualitativeAgent() {
  const { pathname } = useLocation();
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState<"next" | "prev">("next");
  const dispatch = useAppDispatch();
  // const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [pptResponse, setPptResponse] = useState<PptResponseType | null>(null);
  const [searchParams] = useSearchParams();

  // Extract values from URL
  const userId = searchParams.get("userId");
  const threadId = searchParams.get("threadId");
  const workflowId = searchParams.get("workflowId");
  const [loading, setLoading] = useState(true);

  const slides = [
    BannerSlide,
    ()=> <ResearchObjectives researchObject={pptResponse?.research_objectives ?? []} methadology={pptResponse?.methodology_details ?? []}/>,
    ()=> <ParticipantDistribution profile={pptResponse?.participant_profiles ?? []} distribution={pptResponse?.participant_distribution ?? []}/>,
    ()=><AIAdoptation adoptationLevel={pptResponse?.ai_adoption_levels ?? []} aiPatterns={pptResponse?.current_ai_usage_patterns ?? []}/>,
    ()=><ThemeFrequencies themeSignificance={pptResponse?.theme_significance ?? []}/>,
    ()=><EmotionalImpact effects={pptResponse?.cognitive_and_emotional_effects ?? []}/>,
    ()=><IdentifiedRisk riskIdentified={pptResponse?.identified_risks ?? []}/>,
    () => <FutureImpact tableData={pptResponse?.future_capability_scores ?? []} title="Future Impact Scoring Matrix" tableTitle="Future Impact Matrix" interpretations={pptResponse?.future_impact_matrix || []} />,
    ()=><RiskMisinterpretation risk={pptResponse?.risks_misinterpretations_guardrails || []}/>,
    () => <Healthcare data={pptResponse?.future_expectations} title=" Future State of AI in Healthcare" />,
    () => <Recommendations data={pptResponse?.recommendations_set} title="Recommendations" />,
    ThankYouSlide
  ];

 useEffect(() => {
    if (!userId || !threadId || !workflowId) return;

    let interval: NodeJS.Timeout | null = null;
    setLoading(true);

    const isV3 = pathname.includes("quant-agent-v3");

    const fetchStatus = async () => {
      try {
        const response = await dispatch(
          isV3
            ? getPPTStatusV3({ userId, threadId, workflowId })
            : getPPTStatusV2({ userId, threadId, workflowId })
        ).unwrap();

      

        // If still processing → start polling
        if (response?.status === "processing") {
          interval = setInterval(async () => {
            try {
              const retryResponse = await dispatch(
                isV3
                  ? getPPTStatusV3({ userId, threadId, workflowId })
                  : getPPTStatusV2({ userId, threadId, workflowId })
              ).unwrap();

              if (retryResponse?.status !== "processing") {
                setPptResponse(retryResponse.ppt_response);
                setLoading(false);
                if (interval) clearInterval(interval);
              }
            } catch (err) {
              console.error("Error retrying PPT status:", err);
              if (interval) clearInterval(interval);
            }
          }, 2000);
        } else {
          // Immediately ready
          setPptResponse(response.ppt_response);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error calling status API", error);
        setLoading(false);
        if (interval) clearInterval(interval);
      }
    };

    fetchStatus();

    // cleanup on unmount or params change
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [userId, threadId, workflowId, pathname, dispatch]);



  const goNext = () => {
    setDirection("next");
    setIndex((i) => Math.min(i + 1, slides.length - 1));
  };

  const goPrev = () => {
    setDirection("prev");
    setIndex((i) => Math.max(i - 1, 0));
  };

  const SlideComponent = slides[index];

  return (
    <div className="w-full mx-auto bg-white p-3 flex flex-col">
  {loading ? (
    <div className="flex items-center justify-center h-full">
       <PercentageLoader/>
    </div>
  ) : (
    <>
      {/* Slide container */}
      <div className="border-2 border-[#8CC54F] flex flex-col flex-auto py-3 px-5 relative shadow-md">
        <div className="absolute top-0 left-0 inline-flex z-10">
          <span className="w-[20px] h-[14px] bg-[#92D050]"></span>
          <span className="w-[60px] h-[14px] bg-[#8CC54F]"></span>
        </div>
        <div
          key={index}
          className={`relative transition-all duration-500 h-full ease-in-out ${direction === "next"
            ? "animate-slideInFromRight"
            : "animate-slideInFromLeft"
            }`}
        >
          {pptResponse && <SlideComponent />}
        </div>

        <img src={libermanLogo} className="absolute left-0 bottom-0 p-1 bg-white z-10" width={100} height={14} />

        <div className="inline-flex items-center justify-center absolute bottom-0 right-0 bg-[#8CC54F] text-white px-1 py-[5px] z-10">
          <span className="text-xs font-medium tracking-wide">
            {index + 1} / {slides.length}
          </span>
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="mt-2 flex items-center justify-between gap-4">
        <button
          onClick={goPrev}
          disabled={index === 0}
          className="px-2 py-[12px] rounded-md border border-[#8CC54F] text-sm font-medium 
                     text-[#8CC54F] bg-white  disabled:opacity-40 
                     disabled:cursor-not-allowed"
        >
          Previous
        </button>

        <button
          onClick={goNext}
          disabled={index === slides.length - 1}
          className="px-2 py-[12px] rounded-md bg-[#8CC54F] text-white text-sm font-medium 
                      disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </>
  )}
</div>

  );
}
